import axios from "axios";
import router from "@/router";
import { useUserStore } from "@/stores/userStore"; 
import { usePerfilStore } from '@/stores/settings/perfilStore';
import { useNotificationStore } from '@/stores/notifications/useNotificationStore';
import { usePermissionStore } from '@/stores/permissionStore';
import { clinicService } from './clinicService';

const apiClient = axios.create({
  withCredentials: true, // ✅ Cookies e credenciais sempre enviados
});

// ✅ Interceptador para adicionar CSRF token e JWT automaticamente
apiClient.interceptors.request.use((config) => {
  // Sempre adiciona o CSRF token se disponível
  const csrfToken = getCookie("csrftoken");
  if (csrfToken) {
    config.headers["X-CSRFToken"] = csrfToken;
  }

  // Adiciona JWT token se disponível
  const jwtToken = getCookie("access_token");
  if (jwtToken) {
    config.headers["Authorization"] = `Bearer ${jwtToken}`;
  }

  return config;
});

// ✅ Captura o CSRF token apenas para garantir que o backend setou corretamente
export async function fetchCSRFToken() {
  const response = await apiClient.get("/api/v1/auth/csrf");
  return response.data.csrftoken;
}

// ✅ Utilitário para ler cookies
function getCookie(name) {
  const cookies = document.cookie.split("; ");
  for (const cookie of cookies) {
    const [key, value] = cookie.split("=");
    if (key === name) return decodeURIComponent(value);
  }
  return null;
}

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Se for erro de autenticação e não for o refresh em si
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Se já está tentando refresh, aguarda
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
        .then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return apiClient(originalRequest);
        })
        .catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const data = await refreshToken();
        isRefreshing = false;
        processQueue(null, data?.access_token);

        // Atualiza o header Authorization se necessário
        if (data?.access_token) {
          originalRequest.headers['Authorization'] = 'Bearer ' + data.access_token;
        }
        return apiClient(originalRequest);
      } catch (err) {
        isRefreshing = false;
        processQueue(err, null);
        // Se o refresh falhar, faz logout
        await logout();
        router.push({ name: "Login", query: { sessionExpired: true } });
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);

export async function login(username, password) {
  // 🔥 Gera ou recupera o device_id
  let device_id = localStorage.getItem('device_id');
  if (!device_id) {
    device_id = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now();
    localStorage.setItem('device_id', device_id);
  }

  // 🔐 Faz o login enviando também o device_id
  await apiClient.post("/api/v1/auth/", { username, password, device_id });

  const user = await getUser(); // Nome, grupos, notifications

  const perfilStore = usePerfilStore();
  await perfilStore.carregarPerfil(); // Carrega perfil completo (inclusive notifications)

  const notificationStore = useNotificationStore();
  const notificationsEnabled = user.notifications === true || user.notifications_enabled === true;

  // Carrega as permissões do usuário
  const userStore = useUserStore();
  userStore.login(user.first_name || user.username, user.groups || [], user.permissions || [], notificationsEnabled);

  // 🎯 Redirecionamento para monitor se o grupo for "monitor"
  if (user.groups && user.groups.includes('monitor')) {
    router.push('/monitor');
    return user;
  }

  // Só inicia o WebSocket se notificationsEnabled for verdadeiro
  if (notificationsEnabled) {
    setTimeout(() => {
      notificationStore.initWebSocket();
    }, 500);
  }

  // Captura CSRF token após login
  await fetchCSRFToken();

  // ✅ Inicializa o clinicService após login bem-sucedido
  try {
    await initializeClinicService();
  } catch (error) {
    console.warn('⚠️ Erro ao inicializar ClinicService após login:', error);
  }

  return user;
}


export async function verificarTermoAceite() {
  try {
    const perfilStore = usePerfilStore();
    const { data } = await apiClient.get('/api/v1/audit/accept/notify/status/');

    if (data.termo_aceite) {
      perfilStore.setNotificationsStatus(true);
    } else {
      perfilStore.setNotificationsStatus(false);
    }
  } catch (error) {
    // console.error('❌ Erro ao verificar termo de aceite:', error);
  }
}


export async function getUser() {
  try {
    const response = await apiClient.get("/api/v1/auth/user/");
    const user = response.data;
    const userStore = useUserStore();

    const notificationsEnabled = user.notifications === true || user.notifications_enabled === true;

    // Carrega as permissões do usuário
    userStore.login(user.first_name || user.username, user.groups || [], user.permissions || [], notificationsEnabled);

    return user;
  } catch (error) {
    // console.log("getUser erro:", error);
    return null;
  }
}


export async function logout() {
  try {
    await apiClient.post("/api/v1/auth/logout/");
  } catch (error) {
    // console.error("Erro no logout:", error.response?.data || error);
  }

  // Limpa todos os stores do Pinia
  const userStore = useUserStore();
  const perfilStore = usePerfilStore();
  const notificationStore = useNotificationStore();

  // 🛑 Fecha WebSocket
  notificationStore.closeWebSocket();

  // Limpa notificações
  notificationStore.notifications = [];

  // Limpa permissões
  const permissionStore = usePermissionStore();
  permissionStore.clearPermissions();

  userStore.logout();
  perfilStore.limparPerfil();

  sessionStorage.clear();

  if (router.currentRoute.value.path !== "/") {
    router.push("/");
  }
}


export async function refreshToken() {
  try {
    const response = await apiClient.post("/api/v1/auth/token/refresh/");
    return response.data;
  } catch (error) {
    // console.error("Erro ao renovar token:", error.response?.data || error);
    return null;
  }
}

// Função utilitária para obter o usuário atual do localStorage
export function getCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem('user'));
  } catch (e) {
    return null;
  }
}

export default apiClient;

// ✅ Função de inicialização que será executada apenas quando necessário
async function initializeAuthService() {
  try {
    // Verifica se o usuário está autenticado antes de chamar o clinicService
    const jwtToken = getCookie("access_token");
    if (!jwtToken) {
      console.log('ℹ️ Usuário não autenticado, pulando inicialização do ClinicService');
      return;
    }

    // Sempre carrega as informações da clínica quando o authService é inicializado
    const clinicInfo = await clinicService.getClinicInfo();
    return clinicInfo;
  } catch (error) {
    console.warn('⚠️ Erro ao inicializar ClinicService:', error);
    // Não falha a aplicação se não conseguir carregar as informações da clínica
    return null;
  }
}

// Não executa automaticamente - será chamada quando necessário
// initializeAuthService();

// Função para inicializar clinicService após login bem-sucedido
export async function initializeClinicService() {
  return await initializeAuthService();
}
